<template>
  <b-modal
    id="weekly-send-modal"
    title="Review & send"
    size="lg"
    centered
    modal-class="timesheet-modal"
    :busy="sending"
    @show="load"
  >
    <template v-if="sheet">
      <p class="mb-1">
        <strong>{{ projectName }}</strong>
      </p>
      <p class="text-muted">Week of {{ formatDate(sheet.week_start) }}</p>

      <div class="ws-days" data-test="summary">
        <div v-for="day in days" :key="day.date" class="ws-day">
          <span>{{ formatDate(day.date) }}</span>
          <span>{{ dayText(day) }}</span>
        </div>
        <div class="ws-day ws-total">
          <span>Total</span>
          <span data-test="total">{{ formatHours(total) || '0h' }}</span>
        </div>
      </div>

      <h3 class="ws-h3">Send to</h3>
      <div v-if="loading" class="text-center py-2">
        <b-spinner small /> Loading supervisors…
      </div>
      <b-alert v-else-if="error" show variant="danger">
        {{ error }}
        <a href="#" class="alert-link ml-1" @click.prevent="load">Try again</a>
      </b-alert>
      <b-alert
        v-else-if="!supervisors.length"
        show
        variant="warning"
        data-test="no-supervisors"
      >
        No supervisor has been added to this project yet. Ask your company admin
        to add one, then come back to send this week.
      </b-alert>
      <b-form-radio-group
        v-else
        v-model="selectedId"
        stacked
        data-test="supervisors"
      >
        <b-form-radio v-for="s in supervisors" :key="s.id" :value="s.id">
          {{ s.name }}
          <span class="text-muted small">
            {{ [s.position, s.company, s.email].filter(Boolean).join(' · ') }}
          </span>
        </b-form-radio>
      </b-form-radio-group>

      <p class="text-muted small mt-3 mb-0">
        Your supervisor gets an email with a link to approve, amend or reject
        this week. Editing the sheet afterwards cancels the link.
      </p>
      <b-alert v-if="sendError" show variant="danger" class="mt-3 mb-0">
        {{ sendError }}
      </b-alert>
    </template>

    <template #modal-footer="{ cancel }">
      <b-button variant="outline-secondary" :disabled="sending" @click="cancel">
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="sending || !selectedId"
        data-test="send"
        @click="send"
      >
        {{
          sending
            ? 'Sending…'
            : isResubmit
            ? 'Resubmit for approval'
            : 'Send for approval'
        }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import dayjs from 'dayjs'
import {
  dayMinutes,
  formatHours,
  leaveTypeLabel,
} from '@/utils/timesheetHelpers'
import { ownDays, weekTotalMinutes } from '@/utils/myWeekSheet'

const apiMessage = (e, fallback) => {
  const data = e && e.response && e.response.data
  if (data && data.errors) {
    const first = Object.values(data.errors)[0]
    if (first && first[0]) return first[0]
  }
  return (data && data.message) || fallback
}

// The app's Review & send step: the week at a glance, then pick which of the
// project's external supervisors gets the approval link. A sheet that was
// rejected, or is still waiting, goes back through resubmit, which cancels
// the old link.
export default {
  props: {
    sheet: { type: Object, default: null },
    projectName: { type: String, default: '' },
  },

  data() {
    return {
      supervisors: [],
      selectedId: null,
      loading: false,
      error: null,
      sending: false,
      sendError: null,
    }
  },

  computed: {
    days() {
      return ownDays(this.sheet)
    },
    total() {
      return weekTotalMinutes(this.days)
    },
    isResubmit() {
      return (
        !!this.sheet && ['rejected', 'submitted'].includes(this.sheet.status)
      )
    },
  },

  methods: {
    formatHours,

    formatDate(date) {
      return dayjs(date).format('ddd D MMM')
    },

    dayText(day) {
      if (day.leave_type) return leaveTypeLabel(day.leave_type)
      const minutes = dayMinutes(day)
      if (!minutes) return 'No hours'
      return `${day.start}–${day.finish} · ${formatHours(minutes)}`
    },

    async load() {
      if (!this.sheet) return
      this.loading = true
      this.error = null
      this.sendError = null
      try {
        this.supervisors = await this.$store.dispatch(
          'timesheet/myWeekSupervisors',
          this.sheet.id,
        )
        // The supervisor the last link went to, else the only one.
        const last =
          this.sheet.external_request &&
          this.sheet.external_request.supervisor &&
          this.sheet.external_request.supervisor.id
        const match = this.supervisors.find((s) => s.id === last)
        this.selectedId = match
          ? match.id
          : this.supervisors.length === 1
          ? this.supervisors[0].id
          : null
      } catch (e) {
        this.error = apiMessage(e, 'Could not load supervisors.')
      } finally {
        this.loading = false
      }
    },

    async send() {
      const supervisor = this.supervisors.find((s) => s.id === this.selectedId)
      if (!supervisor) return
      this.sending = true
      this.sendError = null
      try {
        const res = await this.$store.dispatch('timesheet/myWeekSend', {
          id: this.sheet.id,
          supervisorId: supervisor.id,
          resubmit: this.isResubmit,
        })
        this.$bvModal.hide('weekly-send-modal')
        this.$emit('sent', { sheet: res && res.data, supervisor })
      } catch (e) {
        this.sendError = apiMessage(e, 'Could not send your timesheet.')
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.ws-days {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 4px 12px;
  margin-bottom: 16px;
}
.ws-day {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #f0f2f4;
}
.ws-total {
  border-bottom: 0;
  font-weight: 700;
}
.ws-h3 {
  font-size: 1rem;
  font-weight: 700;
}
</style>
